import { property } from 'lit-element';
import {
  KatLitElement,
  register,
  event,
  EventEmitter,
} from '../../shared/base';

/**
 * @component {kat-button-group} KatalButtongroup provides an ability to the user to select one of the multiple button options.
 * @status production
 * @theme flo
 * @guideline Do Each button provided is a separate option.
 * @contentProvided <kat-button label="First button"></kat-button>
 * @example SentimentChoice {"value": "", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselected {"value": "neutral", "disabled": false, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 * @example SentimentChoicePreselectedDisabled {"value": "neutral", "disabled": true, "content": "<kat-button label='Strongly Agree' value=\"strong_agree\"><\/kat-button>\r\n<kat-button label='Agree' value=\"agree\"><\/kat-button>\r\n<kat-button label='Neutral' value=\"neutral\"><\/kat-button>\r\n<kat-button label='Disagree' value=\"disagree\"><\/kat-button>\r\n<kat-button label='Strongly Disagree' value=\"strong_disagree\"><\/kat-button>"}
 */
@register('kat-button-group')
export class KatButtonGroup extends KatLitElement {
  /** This attribute will be set on click of any button in the group to that button's value */
  @property()
  value?: string;

  /** When kat-button-group is disabled, no buttons can be clicked */
  @property()
  disabled?: boolean;

  /** Triggered when a user chooses an option. */
  @event('change', true)
  private _change: EventEmitter<{ value: string }>;

  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    this.addEventListener('click', this._handleClick);

    this.observeChildren(this, () => {
      this.requestUpdate();
    });
  }

  _handleClick(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    if (this.disabled) {
      // leaving this here for safety, however this should never be called
      // because the kat-buttons themselves are being marked as disabled.
      /* istanbul ignore next */
      return;
    }

    const selectedButton = e.target.closest('*[value]');

    // There doesn't seem to be a way to test for this exception in headless chrome
    /* istanbul ignore next */
    if (!selectedButton) {
      /* istanbul ignore next */
      throw new Error(
        'Expected kat-button-group buttons to have a value attribute'
      );
    }

    const selectedButtonValue = selectedButton.value;
    const currentValue = this.value;

    if (selectedButtonValue === currentValue) {
      return;
    }

    this.value = selectedButtonValue;

    if (e.type !== 'change' && e.type !== 'input') {
      this._change.emit({ value: selectedButtonValue });
    }
  }

  render() {
    const selectedValue = this.value;
    const disabled = this.disabled;

    const childButtons = Array.from(this.childNodes).filter(
      e => e.tagName === 'KAT-BUTTON'
    );

    childButtons.forEach(buttonElement => {
      buttonElement.disabled = disabled;
      if (buttonElement.value === selectedValue) {
        buttonElement.classList.add('selected');
      } else {
        buttonElement.classList.remove('selected');
      }
    });

    // prevent lit from blowing away light dom
    return super.render();
  }
}
